import useSWR from "swr"

import { Datasets } from "../../../../constants/enums"
import { AppError } from "../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../errors/recognisedErrors"
import { useGetUserData } from "../../../../hooks/useGetUserData"
import { getClientProtected } from "../../clients/clients"
import { FULL_DEVICE } from "../../urls/hybrid"

export interface IFullEVDCDevice {
    header: IEVDCHeader
    core: IEVDCCoreDetails
    technical: IEVDCTechnicalDetails
    validation: IEVDCValidationDetails
}

interface IEVDCHeader {
    "Device Ref": string
    Status: string
    "Published At": string
}

export interface IEVDCCoreDetails {
    Make: string
    "Model Number": string
    "Number of Phases": number
    "EVCP Type": string
}

export interface IEVDCTechnicalDetails {
    "Voltage (V)": number
    "Total Input Rated Current (A)": number
    "Total Input Rated Power (kVA)": number
    "Total Maximum Demand Current (A)": number
    "Total Maximum Demand Power (kVA)": number
    "Module Input Rated Current (A)": number
    "Module Input Rated Power (kVA)": number
}

export interface IEVDCValidationDetails {
    "Worst Case Power Level %": number | null
    "Applicable Harmonic Emission Standard": string | null
    "SSC Minimum 3 Phase (kVA)": number | null
    "Charger System Rated Less Than or Equal to 75A (Ω)": number | null
    "Charger System Rated Greater Than or Equal to 75A (Ω)": number | null
    "Information Provided on Voltage Fluctuation and Flicker": string | null
    "In Accordance With EN61000-3-11 for Voltage Fluctuation and Flicker":
        | string
        | null
    "Maximum Impedance for Voltage Fluctuation and Flicker": number | null
}

export interface IFullHPDevice {
    header: IHPHeader
    core: IHPCoreDetails
    technical: IHPTechnicalDetails
    validation: IHPValidationDetails
    backUpHeater: IHPSubDeviceDetails
    boostHeater: IHPSubDeviceDetails
    waterHeater: IHPSubDeviceDetails
    heatPump: IHPSubDeviceDetails
}

interface IHPHeader {
    "Device Ref": string
    Status: string
    "Published At": string
    backUpHeaterStatus: string
    boostHeaterStatus: string
    waterHeaterStatus: string
    heatPumpStatus: string
}

interface IHPCoreDetails {
    Make: string
    Model: string
    "Model Reference": string
    "Heat Pump Type": string
    Function: string
    "Number of Phases": number
}

interface IHPTechnicalDetails {
    "Voltage (V)": number
    Module: string
    "A-7/ W35": number
    "A-7/ W45": number
    "A-7/ W55": number
    "A-7/ W65": number
    "Total Input Rated Current (A)": number
    "Total Input Rated Power (kVA)": number
    "Total Maximum Demand Current (A)": number
    "Total Maximum Demand Power (kVA)": number
}

export interface IHPValidationDetails {
    "Class A limits of IEC 61000-3-2 or Zpcc > Zref?": string
    "Technical Requirements of IEC 61000-3-3?": string
    "Both Standards for All Parts of System?": string
    "Total System Maximum Demand ≤32A?": string
    "Connect & Notify?": string
    "Apply to Connect?": string
    "In Accordance With EN61000-3-12": string
    "SSC Minimum 3 Phase (kVA)": number
    "SSC Minimum Single Phase (kVA)": number
    "Minimum Rsce calculated": number
    "Fault level calculated using minimum Rsce (MVA)": number
    "Impedance at PCC (Ω)": number
    "In Accordance With EN61000-3-11": string
    "Zmax (Ω)": number
}

export interface IHPSubDeviceDetails {
    "In Accordance With EN61000-3-12": string
    "SSC Minimum 3 Phase (kVA)": number
    "In Accordance With EN61000-3-11": string
    "Zmax (Ω)": number
}

export interface IUseGetFullDevice {
    data: IFullEVDCDevice | undefined
    isLoading: boolean
    error: AppError
    mutate: () => void
    isValidating: boolean
}

/**
 * Hook for getting full LCT device data to display on assess device page.
 * @returns {IUseGetFullDevice} - SWR data, loading and error states.
 */
export const useGetFullDevice = (
    deviceId: string,
    dataset: Datasets
): IUseGetFullDevice => {
    const { isConfiguring } = useGetUserData()

    const { data, error, isLoading, mutate, isValidating } = useSWR(
        isConfiguring ? null : FULL_DEVICE(deviceId, dataset),
        async (url: string): Promise<IFullEVDCDevice> => {
            try {
                return (await getClientProtected(url)) as IFullEVDCDevice
            } catch {
                throw invalidApiDataError(
                    "An error occurred while fetching the device"
                )
            }
        },
        { keepPreviousData: true }
    )

    return {
        data,
        isLoading: isLoading || isConfiguring, // See notes in docstring
        error,
        mutate,
        isValidating,
    }
}
